import React, { useState } from "react";
import { ClipboardCheck, Package } from "lucide-react";
import { cn } from "lib/utils";
import BulkEditApi from "./BulkEditApi";
import PendingProducts from "../Catalouge/Product/PendingProducts";

// Bulk-edit shell. Two tabs share the page: the live-product editor (default)
// and the pending-review queue. Tab state is kept in local state — no URL or
// persistence — to mirror the rest of the bulk-edit page's behavior.

const TABS = Object.freeze({
  LIVE: "live",
  PENDING: "pending",
});

// Order matters: the sliding indicator translates by the active tab's index,
// so the array order is also the visual left-to-right order.
const TAB_OPTIONS = [
  {
    key: TABS.LIVE,
    label: "Live Products",
    icon: Package,
    dotClass: "bg-emerald-500",
  },
  {
    key: TABS.PENDING,
    label: "Pending Review",
    icon: ClipboardCheck,
    dotClass: "bg-amber-500",
  },
];

// `initialTab` lets entry routes (e.g. legacy `/product/pending`) drop the
// user straight onto the Pending tab without forcing a separate page.
const BulkParentPage = ({ initialTab = TABS.LIVE }) => {
  const safeInitial = initialTab === TABS.PENDING ? TABS.PENDING : TABS.LIVE;
  const [activeTab, setActiveTab] = useState(safeInitial);

  // No flex/overflow wrapper around the children — both BulkEditApi and
  // PendingProducts manage their own layout (including the table's horizontal
  // scroll). Adding a flex container here breaks their overflow contexts.
  return (
    <>
      <TabStrip activeTab={activeTab} onTabChange={setActiveTab} />
      {activeTab === TABS.LIVE ? <BulkEditApi /> : <PendingProducts />}
    </>
  );
};

const TabStrip = ({ activeTab, onTabChange }) => {
  const activeIndex = Math.max(
    TAB_OPTIONS.findIndex((t) => t.key === activeTab),
    0,
  );

  return (
    <div className="border-b border-border bg-card">
      <div className="px-4 py-2">
        <div
          role="tablist"
          aria-label="Bulk products view"
          // Equal-width 2-column grid so the sliding indicator can translate
          // by exactly the active tab's index without measuring widths.
          className="relative inline-grid grid-cols-2 rounded-lg bg-muted p-1"
        >
          {/* Sliding active-tab background. Lives behind the buttons (z-0)
              and animates via transform between the two tab slots. */}
          <span
            aria-hidden="true"
            className={cn(
              "absolute top-1 bottom-1 left-1 z-0",
              "w-[calc(50%-0.25rem)] rounded-md bg-background shadow-sm",
              "transition-transform duration-200 ease-out",
            )}
            style={{ transform: `translateX(${activeIndex * 100}%)` }}
          />
          {TAB_OPTIONS.map((tab) => (
            <TabButton
              key={tab.key}
              icon={tab.icon}
              label={tab.label}
              dotClass={tab.dotClass}
              isActive={activeTab === tab.key}
              onClick={() => onTabChange(tab.key)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const TabButton = ({ icon: Icon, label, dotClass, isActive, onClick }) => (
  <button
    type="button"
    role="tab"
    aria-selected={isActive}
    onClick={onClick}
    // z-10 so the button sits above the sliding indicator. Reset default
    // browser <button> chrome so the segmented look comes purely from the
    // grid container + sliding indicator.
    className={cn(
      "relative z-10 inline-flex items-center justify-center gap-2",
      "rounded-md border-0 bg-transparent",
      "px-4 py-1.5 text-sm font-medium cursor-pointer transition-colors duration-150",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      isActive
        ? "text-foreground"
        : "text-muted-foreground hover:text-foreground",
    )}
  >
    <span
      className={cn(
        "size-1.5 rounded-full transition-opacity",
        dotClass,
        isActive ? "opacity-100" : "opacity-60",
      )}
      aria-hidden="true"
    />
    <Icon className="size-4" aria-hidden="true" />
    {label}
  </button>
);

export default BulkParentPage;
